import { useState } from 'react';
import { Check, Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminResetPassword } from '../../services/httpServices/queries';

interface ChangePasswordModalProps {
  user: { id: string; username: string; firstName?: string; lastName?: string } | null;
  onClose: () => void;
}

/**
 * Set a new password for a user (admin reset).
 * Clicking the backdrop does not dismiss the dialog.
 */
export function ChangePasswordModal({ user, onClose }: ChangePasswordModalProps) {
  const adminResetPassword = useAdminResetPassword();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!user) return null;

  const tooShort = password.length < 6;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async () => {
    if (tooShort) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await adminResetPassword.mutateAsync({ id: user.id, newPassword: password });
      toast.success('Password updated successfully');
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update password');
    }
  };

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
        <h3 className="text-lg font-semibold text-text">Change Password</h3>
        <p className="text-xs text-text-muted">
          Set a new password for <strong>{displayName}</strong> (@{user.username}).
        </p>
        <div>
          <label className="block text-sm font-medium text-text mb-1">New Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-10 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-xs text-text-muted mt-1">Minimum 6 characters</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-text mb-1">Confirm Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full pl-9 pr-10 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
              tabIndex={-1}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {mismatch && <p className="text-xs text-danger mt-1">Passwords do not match</p>}
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={adminResetPassword.isPending || tooShort || password !== confirmPassword}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {adminResetPassword.isPending && <Loader2 size={16} className="animate-spin" />}
            <Check size={16} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
